import { API_CONNECTOR, CONNECTOR_LEVELS } from '@avalara/bat-apps-shared/constants';

import { newPaymentMethodWording } from '../components/shared/payment/payment-method-selector';
import analytics from '../lib/analytics';

import type { Action } from '@avalara/types/actions';
import type { PaymentMethodStatus } from '@avalara/types/biztech';
import type { CreditCardTypes } from '@avalara/types/index';
import type { AccountState } from '@avalara/types/reducer-states';

export const defaultState: AccountState = {
  accountId: '',
  billingContact: null,
  cam: null,
  camOwned: false,
  companyName: '',
  customerAccountId: '',
  isLoggedIn: false,
  licenseKey: null,
  name: null,
  numFreeTrialObligations: 0,
  // default the account's connector to API
  connector: API_CONNECTOR,
  supportedConnector: true, // toggle off if recieve unsupported connector
  paymentMethods: [],
  primaryContactEmail: '',
  user: null,
  obligations: [],
  selectedPaymentMethod: null,
  isAdmin: false,
  referrer: null,
  returnUrl: null,
};

// biome-ignore lint/style/useDefaultParameterLast: this is the order in which Redux wants it
export default function accountReducer(state: AccountState = defaultState, action: Action): AccountState {
  switch (action.type) {
    case 'RECEIVE_USER': {
      // accountId should only ever live in account state post login
      const {
        accountId,
        billingContact,
        cam,
        camOwned,
        companyName,
        customerAccountId,
        isAdmin,
        name,
        numFreeTrialObligations,
        obligations,
        primaryContactEmail,
        user,
        supportedConnectors,
        storedConnector,
      } = action;

      analytics.set('accountId', accountId);

      // There is an edge case that an account has multiple supported connectors. Just default to the
      // first one, as this should not often happen.
      // If the account has no supported connectors, default to the API connector
      let connector = API_CONNECTOR;

      if (supportedConnectors?.length) {
        [connector] = supportedConnectors;
        // connector found in billing account, no need to keep this value in localStorage
        if (window.localStorage) {
          window.localStorage.removeItem(`ava-connector-id-signup-for-${accountId}`);
        }
      } else if (storedConnector) {
        connector = storedConnector;
      }

      // NOTE: connector may be undefined here, if unsupported connector. This is expected behavior
      //   and the container should disable buy in this case
      if (!connector) {
        return { ...state, accountId, billingContact, supportedConnector: false };
      }

      const metadata =
        (connector.level != null ? CONNECTOR_LEVELS[connector.level] : undefined) || CONNECTOR_LEVELS['Level 0'];
      // NOTE(Jeremy): we should avoid updating the connector if it is already set (that is, if it's not set to the default)
      const shouldAvoidUpdatingConnector = state.connector.name !== API_CONNECTOR.name;

      return {
        ...state,
        accountId,
        billingContact,
        cam,
        camOwned,
        companyName,
        connector: shouldAvoidUpdatingConnector ? state.connector : { ...connector, metadata },
        customerAccountId,
        isLoggedIn: true,
        name,
        numFreeTrialObligations: numFreeTrialObligations || 0,
        obligations,
        primaryContactEmail,
        user,
        isAdmin,
      };
    }
    case 'SET_PARTNER_CONNECTOR': {
      return { ...state, connector: action.connector };
    }
    case 'ADD_CREDIT_CARD': {
      const newPaymentMethod = {
        defaultPaymentMethod: false,
        Id: action.paymentMethodToken,
        PaymentMethodStatus: 'Active' as PaymentMethodStatus,
        Type: 'NewCreditCard' as CreditCardTypes,
        CreditCardExpirationMonth: 0,
        CreditCardExpirationYear: 0,
        CreditCardType: '',
        CreditCardMaskNumber: '',
      };

      // if a new credit card has already been added, replace it
      const paymentMethods = state.paymentMethods.filter((pm) => pm.Type !== 'NewCreditCard');

      return {
        ...state,
        paymentMethods: paymentMethods.concat([newPaymentMethod]),
        selectedPaymentMethod: newPaymentMethod,
      };
    }
    case 'RECEIVE_PAYMENT_METHODS': {
      return {
        ...state,
        paymentMethods: action.paymentMethods,
        selectedPaymentMethod: action.paymentMethods.find((a) => a.defaultPaymentMethod) || null,
      };
    }
    case 'SET_ACCOUNT_SETUP_RETRY_KEY': {
      return { ...state, retryKey: action.retryKey };
    }
    case 'SET_CONNECTOR': {
      const { connector } = action;
      const metadata = connector.level != null ? CONNECTOR_LEVELS[connector.level] : undefined;
      return { ...state, connector: { ...connector, metadata } };
    }
    case 'SET_SELECTED_PAYMENT_METHOD': {
      let paymentMethod = null;
      if (
        action.paymentMethodId !== newPaymentMethodWording &&
        Boolean(action.paymentMethodId) &&
        state.paymentMethods.length !== 0
      ) {
        paymentMethod =
          state.paymentMethods.find((a) => a.Id === action.paymentMethodId) || state.selectedPaymentMethod;
      }
      return {
        ...state,
        selectedPaymentMethod: paymentMethod,
      };
    }
    case 'SET_REFERRER':
      return {
        ...state,
        referrer: action.referrer,
      };
    case 'SET_RETURN_URL':
      return {
        ...state,
        returnUrl: action.returnUrl,
      };
    default:
      return state;
  }
}
